import { EducateType } from '@/types/educate';
import type {
  AudiobookContent,
  CaseStudyContent,
  ConceptWalkthroughContent,
  DecisionTreeContent,
  DiagnosticContent,
  EducateContent,
  Flashcard,
  PodcastContent,
  QuizQuestion,
  ScenarioLedContent
} from '@/types/educate';

export type EducateUnit =
  | {
      id: string;
      kind: 'reading';
      label: string;
      title: string;
      paragraphs: string[];
      flashcards: Flashcard[];
      quiz: QuizQuestion[];
    }
  | { id: string; kind: 'prose'; label: string; title: string; body: string }
  | { id: string; kind: 'list'; label: string; title: string; items: string[] }
  | { id: string; kind: 'beat'; label: string; title: string; point: string; explanation: string }
  | {
      id: string;
      kind: 'hypothesis';
      label: string;
      title: string;
      cause: string;
      test: string;
      reasoning: string;
      isRootCause: boolean;
    }
  | { id: string; kind: 'check'; label: string; title: string; question: QuizQuestion }
  | {
      id: string;
      kind: 'casePage';
      label: string;
      title: string;
      stamp?: string;
      narrative: string;
      analysis: string;
      modelAnswer: string;
    }
  | {
      id: string;
      kind: 'slide';
      label: string;
      title: string;
      bullets: string[];
      script: string;
    }
  | {
      id: string;
      kind: 'podcastTurn';
      label: string;
      title: string;
      speaker: 'host' | 'expert';
      text: string;
    }
  | {
      id: string;
      kind: 'decisionNode';
      label: string;
      title: string;
      situation: string;
      choices: Array<{ label: string; consequence: string; nextNodeId: string | null }>;
      terminal: boolean;
      outcome?: string;
    };

export type UnitKind = EducateUnit['kind'];

const asArray = <T>(value: T[] | undefined | null): T[] => (Array.isArray(value) ? value : []);

const conceptWalkthroughUnits = (content: ConceptWalkthroughContent): EducateUnit[] =>
  asArray(content.sections).map((section, index) => ({
    id: `section-${index}`,
    kind: 'reading',
    label: `Section ${index + 1}`,
    title: section.title,
    paragraphs: asArray(section.paragraphs),
    flashcards: asArray(section.flashcards),
    quiz: asArray(section.quiz)
  }));

const scenarioLedUnits = (content: ScenarioLedContent): EducateUnit[] => {
  const units: EducateUnit[] = [
    {
      id: 'situation',
      kind: 'prose',
      label: 'The situation',
      title: 'The situation',
      body: content.situation ?? ''
    },
    ...asArray(content.teachingBeats).map((beat, index): EducateUnit => ({
      id: `beat-${index}`,
      kind: 'beat',
      label: `Insight ${index + 1}`,
      title: beat.point,
      point: beat.point,
      explanation: beat.explanation
    })),
    {
      id: 'resolution',
      kind: 'prose',
      label: 'Resolution',
      title: 'How it resolves',
      body: content.resolution ?? ''
    }
  ];

  units.push(checkUnit(content.check));
  return units;
};

const decisionTreeUnits = (content: DecisionTreeContent): EducateUnit[] =>
  asArray(content.nodes).map((node, index) => ({
    id: `node-${node.id}`,
    kind: 'decisionNode',
    label: `Decision ${index + 1}`,
    title: node.title ?? `Decision ${index + 1}`,
    situation: node.situation ?? '',
    choices: asArray(node.choices),
    terminal: Boolean(node.terminal) || asArray(node.choices).length === 0,
    outcome: node.outcome
  }));

const caseStudyUnits = (content: CaseStudyContent): EducateUnit[] => {
  const units: EducateUnit[] = [];

  if (content.brief) {
    units.push({
      id: 'brief',
      kind: 'prose',
      label: 'Case brief',
      title: content.title ?? 'Case brief',
      body: content.brief
    });
  }

  asArray(content.pages).forEach((page, index) => {
    units.push({
      id: `page-${index}`,
      kind: 'casePage',
      label: `Moment ${index + 1}`,
      title: page.title ?? `Moment ${index + 1}`,
      stamp: asArray(content.stamps)[index],
      narrative: page.narrative,
      analysis: page.analysis,
      modelAnswer: page.modelAnswer
    });
  });

  if (content.synthesis) {
    units.push({
      id: 'synthesis',
      kind: 'prose',
      label: 'Synthesis',
      title: 'Synthesis',
      body: content.synthesis
    });
  }

  return units;
};

const diagnosticUnits = (content: DiagnosticContent): EducateUnit[] => [
  {
    id: 'system',
    kind: 'prose',
    label: 'The system',
    title: 'How it should work',
    body: content.systemDescription ?? ''
  },
  {
    id: 'symptoms',
    kind: 'list',
    label: 'Symptoms',
    title: 'What we observe',
    items: asArray(content.symptoms)
  },
  ...asArray(content.hypotheses).map((hypothesis, index): EducateUnit => ({
    id: `hypothesis-${index}`,
    kind: 'hypothesis',
    label: `Hypothesis ${index + 1}`,
    title: hypothesis.cause,
    cause: hypothesis.cause,
    test: hypothesis.test,
    reasoning: hypothesis.reasoning,
    isRootCause: Boolean(hypothesis.isRootCause)
  })),
  {
    id: 'resolution',
    kind: 'prose',
    label: 'Resolution',
    title: 'The fix',
    body: content.resolution ?? ''
  },
  checkUnit(content.check)
];

const audiobookUnits = (content: AudiobookContent): EducateUnit[] =>
  asArray(content.slides).map((slide, index) => ({
    id: `slide-${index}`,
    kind: 'slide',
    label: `Slide ${index + 1}`,
    title: slide.title,
    bullets: asArray(slide.bullets),
    script: slide.script ?? ''
  }));

const podcastUnits = (content: PodcastContent): EducateUnit[] =>
  asArray(content.turns).map((turn, index) => ({
    id: `turn-${index}`,
    kind: 'podcastTurn',
    label: `Turn ${index + 1}`,
    title: turn.speaker === 'host' ? 'Host' : 'Expert',
    speaker: turn.speaker === 'host' ? 'host' : 'expert',
    text: turn.text ?? ''
  }));

const checkUnit = (question?: QuizQuestion): EducateUnit => ({
  id: 'check',
  kind: 'check',
  label: 'Knowledge check',
  title: 'Knowledge check',
  question:
    question && Array.isArray(question.options) && question.options.length > 0
      ? question
      : {
          question: 'Ready to wrap up?',
          options: ['Yes, I have worked through the module'],
          correctIndex: 0,
          feedback: 'No knowledge check was authored for this module.'
        }
});

export const buildUnits = (educateType: EducateType, content: EducateContent): EducateUnit[] => {
  if (!content || typeof content !== 'object') return [];

  switch (educateType) {
    case EducateType.CONCEPT_WALKTHROUGH:
      return conceptWalkthroughUnits(content as ConceptWalkthroughContent);
    case EducateType.SCENARIO_LED:
      return scenarioLedUnits(content as ScenarioLedContent);
    case EducateType.DECISION_TREE:
      return decisionTreeUnits(content as DecisionTreeContent);
    case EducateType.CASE_STUDY:
      return caseStudyUnits(content as CaseStudyContent);
    case EducateType.DIAGNOSTIC:
      return diagnosticUnits(content as DiagnosticContent);
    case EducateType.AUDIOBOOK:
      return audiobookUnits(content as AudiobookContent);
    case EducateType.PODCAST:
      return podcastUnits(content as PodcastContent);
    default:
      return [];
  }
};

export const isBranching = (educateType: EducateType): boolean =>
  educateType === EducateType.DECISION_TREE;

export const isNarrated = (educateType: EducateType): boolean =>
  educateType === EducateType.AUDIOBOOK || educateType === EducateType.PODCAST;

export const EDUCATE_TYPE_LABEL: Record<EducateType, string> = {
  [EducateType.CONCEPT_WALKTHROUGH]: 'Concept Walkthrough',
  [EducateType.SCENARIO_LED]: 'Scenario-Led Learning',
  [EducateType.DECISION_TREE]: 'Decision Tree',
  [EducateType.CASE_STUDY]: 'Case Study',
  [EducateType.DIAGNOSTIC]: 'Diagnostic / Troubleshooting',
  [EducateType.AUDIOBOOK]: 'Audiobook',
  [EducateType.PODCAST]: 'Podcast'
};
